/**
 * Hat model for the web client (SPEC.md "One person, multiple hats").
 *
 * The account's capabilities decide which "hats" it can wear: My Projects
 * (building), My Students (mentor), My Kids (parent). The server derives the
 * ordered `hats` list on the me/session payload; this module turns that list
 * into the things the UI needs: the label, icon, and home route for each hat,
 * the mapping from a URL back to the hat it belongs to (so deep links land in
 * the right hat), and the rules for which hat is active and whether the switcher
 * shows at all.
 *
 * Pure data logic, no React and no I/O, so the switcher-visibility matrix and
 * the active-hat resolution can be tested directly.
 */

import type { Capabilities, Hat, Role } from '@proteam/shared';

import type { IconName } from '../ui/index.js';

export interface HatMeta {
  /** Product label shown in the switcher. Plain words, no jargon. */
  label: string;
  icon: IconName;
  /** The default route this hat opens to. */
  home: string;
}

/** Per-hat presentation and home route, in the canonical hat order. */
export const HAT_META: Readonly<Record<Hat, HatMeta>> = {
  projects: { label: 'Builder', icon: 'build', home: '/build' },
  students: { label: 'Mentor', icon: 'dashboard', home: '/mentor' },
  kids: { label: 'Parent', icon: 'home', home: '/parent' },
};

/** The route a hat opens to. */
export function hatHome(hat: Hat): string {
  return HAT_META[hat].home;
}

/**
 * Which hat a path belongs to, or null for a neutral path (settings, showcase,
 * status screens). Used to keep the active hat in sync with deep links.
 */
export function hatForPath(pathname: string): Hat | null {
  if (pathname === '/build' || pathname.startsWith('/build/')) return 'projects';
  if (pathname === '/mentor' || pathname.startsWith('/mentor/')) return 'students';
  if (pathname === '/parent' || pathname.startsWith('/parent/')) return 'kids';
  return null;
}

/**
 * The hat an account lands on by default, from its role. The role only picks the
 * default landing surface (SPEC.md); the hats an account actually has come from
 * its capabilities.
 */
export function defaultHatForRole(role: Role): Hat {
  switch (role) {
    case 'mentor':
      return 'students';
    case 'parent':
      return 'kids';
    case 'student':
      return 'projects';
  }
}

/**
 * The active hat, given the hats the account has, its role, and any persisted
 * choice. A persisted choice wins only when it is still one of the account's
 * hats; otherwise we fall back to the role default (when that hat exists) or the
 * first available hat. Fails safe: an account with no hats resolves to its role
 * default so nothing downstream sees a null.
 */
export function resolveActiveHat(available: readonly Hat[], role: Role, stored: string | null): Hat {
  const isAvailable = (value: string | null): value is Hat =>
    value !== null && (available as readonly string[]).includes(value);

  if (isAvailable(stored)) return stored;
  const preferred = defaultHatForRole(role);
  if (available.includes(preferred)) return preferred;
  return available[0] ?? preferred;
}

/**
 * Whether to render the switcher at all. Only accounts with more than one hat
 * get a switcher; a single-hat account sees none (SPEC.md).
 */
export function shouldShowSwitcher(available: readonly Hat[]): boolean {
  return available.length >= 2;
}

/** The minimal account shape the home resolver needs. */
export interface HatHomeUser {
  role: Role;
  capabilities: Capabilities;
}

/**
 * The route to open for a signed-in account, honoring a persisted hat choice.
 * Reads only the hats from capabilities, defensively, so a payload without
 * capabilities still resolves to a sensible role home instead of throwing.
 */
export function homeForUser(user: HatHomeUser, stored: string | null): string {
  const available = user.capabilities?.hats ?? [];
  return hatHome(resolveActiveHat(available, user.role, stored));
}
